<template>
  <aha-modal
    title="Update card info"
    ref="update-card-info-popup"
    id="update-card-info-popup"
    v-if="visible"
    action-position="justify-center"
    @hide="hide()"
    size="lg"
  >
    <div class="max-w-md card-form">
      <!-- Card number -->
      <div class="label-card">
        Card number:
      </div>
      <div class="card-input-wrapper">
        <div id="card-number" />
        <i class="fas fa-address-card card-input-icon" />
      </div>
      <p class="mb-3 card-error-text" v-if="textErrorCard">
        {{ textErrorCard }}
      </p>
      <!-- Card expiry -->
      <div class="label-card">
        Expiration date:
      </div>
      <div class="card-input-wrapper">
        <div id="card-expiry" />
      </div>
      <p class="mb-3 card-error-text" v-if="textErrorExpired">
        {{ textErrorExpired }}
      </p>
      <!-- Card CVC -->
      <div class="label-card">
        CVV / CVC:
      </div>
      <div class="card-input-wrapper">
        <div id="card-cvc" />
        <i class="fas fa-address-card card-input-icon" />
      </div>
      <p class="mb-3 card-error-text" v-if="textErrorCvc">
        {{ textErrorCvc }}
      </p>
      <!-- Secure notice -->
      <p class="text-center px-sm-4 secure-notice">
        <i class="pr-1 fas fa-lock fa-fw" />
        We do not store your card information. It will be handled securely by Stripe
        - our global trusted payment partner.
      </p>
    </div>
    <template #action>
      <aha-button
        @click="handleUpdateCardInfo()"
        :disabled="checkDisableUpdate"
        :submitting="loadingConfirmUpdateCard"
      >
        <span class="update-card-btn">OK</span>
        <aha-icon
          name="check"
          viewBox="0 0 24 24"
          width="16"
          height="16"
          class="ml-1"
          v-if="!loadingConfirmUpdateCard"
        />
      </aha-button>
    </template>
    <div class="m-2 text-center text-red-50" v-if="error">
      {{ error }}
    </div>
  </aha-modal>
</template>
<script>
import Util from '@/util'
import Config from '@/config'
import EventBus from '@/eventBus'
import stripeApi from '@/api/stripe'

export default {
  name: 'UpdateCardInfo',
  data() {
    return {
      visible: false,
      card: null,
      cardExpiry: null,
      cardCvc: null,
      currentCard: {},
      userOldCard: true,
      textError: '',
      textErrorCard: '',
      textErrorExpired: '',
      textErrorCvc: '',
      isValidCard: false,
      isValidExpired: false,
      isValidCvc: false,
      loadingConfirmUpdateCard: false,
      error: false,
      disabled: true,
    }
  },
  computed: {
    checkDisableUpdate() {
      if (this.disabled || this.loadingConfirmUpdateCard) {
        return true
      }
      if (this.userOldCard) {
        return false
      }
      if (!this.isValidCard || !this.isValidCvc || !this.isValidCvc) {
        return true
      }
      return false
    },
  },
  mounted() {
    this.visible = true
    if (this.$store.state.currentUser.id) {
      setTimeout(() => {
        this.getStripeForm()
      })
    }
  },
  methods: {
    getStripeForm() {
      if (this.$store.state.currentUser.stripeSource === 'australia') {
        this.stripe = window.Stripe(Config.stripePublicKey)
      } else {
        this.stripe = window.Stripe(Config.singaporeStripePublishableKey)
      }
      const elementStyles = {
        base: {
          color: '#32325D',
          fontWeight: 500,
          fontSize: '16px',
          '::placeholder': {
            color: '#CFD7DF',
          },
          ':-webkit-autofill': {
            color: '#e39f48',
          },
        },
        invalid: {
          color: '#FF4954',
          '::placeholder': {
            color: '#FFCCA5',
          },
        },
      }
      const elementClasses = {
        focus: 'focused',
        empty: 'empty',
        invalid: 'invalid',
      }
      const elements = this.stripe.elements()
      this.card = elements.create('cardNumber', {
        style: elementStyles,
        classes: elementClasses,
      })
      this.card.mount('#card-number')
      this.cardExpiry = elements.create('cardExpiry', {
        style: elementStyles,
        classes: elementClasses,
      })
      this.cardExpiry.mount('#card-expiry')
      this.cardCvc = elements.create('cardCvc', {
        style: elementStyles,
        classes: elementClasses,
      })
      this.cardCvc.mount('#card-cvc')
      this.card.addEventListener('change', ({ error, empty }) => {
        this.disabled = false
        if (error) {
          this.textErrorCard = error.message
          this.isValidCard = false
        } else {
          this.isValidCard = !empty
          this.userOldCard = false
          this.textErrorCard = ''
        }
      })
      this.cardExpiry.addEventListener('change', ({ error, empty }) => {
        this.disabled = false
        if (error) {
          this.textErrorExpired = error.message
          this.isValidExpired = false
        } else {
          this.isValidExpired = !empty
          this.userOldCard = false
          this.textErrorExpired = ''
        }
      })
      this.cardCvc.addEventListener('change', ({ error, empty }) => {
        this.disabled = false
        if (error) {
          this.textErrorCvc = error.message
          this.isValidCvc = false
        } else {
          this.isValidCvc = !empty
          this.userOldCard = false
          this.textErrorCvc = ''
        }
      })
    },
    hide() {
      this.visible = false
      this.$emit('handleCloseUpdateCardInfo')
      Util.gaTrackEvent(
        'User Plan',
        'User update card info',
        'User close popup update card info',
      )
    },
    handleUpdateCardInfo() {
      if (this.loadingConfirmUpdateCard) return
      this.loadingConfirmUpdateCard = true
      Util.gaTrackEvent(
        'User Plan',
        'User update card info',
        'User confirm update card info',
      )
      this._createStripePaymentMethod()
    },
    _createStripePaymentMethod() {
      this.stripe.createPaymentMethod({
        type: 'card',
        card: this.card,
      }).then((result) => {
        if (result.error) {
          this.error = result.error.message
          this.loadingConfirmUpdateCard = false
        } else if (result.paymentMethod) {
          const { paymentMethod } = result
          this._updateCardInfo(paymentMethod)
        }
      })
    },
    async _updateCardInfo(paymentMethod) {
      this.error = false
      try {
        await stripeApi.updateCard({
          payment_method: paymentMethod.id,
        })
        EventBus.$emit('handleTurnOnAutoRenew', true)
        this.loadingConfirmUpdateCard = false
        this.hide()
        this.$emit('handleCloseUpdateCardInfo', paymentMethod.card)
        Util.gaTrackEvent(
          'User Plan',
          'User update card info',
          'User update card info success',
        )
      } catch (err) {
        if (err.body && err.body.err) {
          this.error = err.body.err.raw.message
        } else {
          this.error = 'Something went wrong, please contact us for support'
        }
        this.loadingConfirmUpdateCard = false
        Util.gaTrackEvent(
          'User Plan',
          'User update card info',
          'User click on OK update card info',
        )
      }
    },
  },
}
</script>
<style lang="scss" scoped>
.card-form {
  .label-card {
    margin-bottom: 2px;
    color: #666;
    font-weight: bold;
  }
  .card-input-wrapper {
    position: relative;
    #card-number,
    #card-expiry,
    #card-cvc {
      border: 1px solid #ddd;
      height: 40px;
      padding: 10px;
      border-radius: 5px;
      margin-bottom: 16px;
    }
    .card-input-icon {
      position: absolute;
      top: 0;
      right: 0;
      padding: 11.75px;
      color: #666666;
    }
  }
  .card-error-text {
    color: #FF4954;
    margin-top: -10px;
    font-weight: bold;
  }
  .secure-notice {
    font-size: 13px;
    color: #479a47;
    .fas {
      color: inherit;
    }
  }

}
.update-card-btn {
    min-width: 1.5rem;
  }
</style>
